// Interfaz de "Jugar con amigos": crear/unirse a sala (código + QR) y sala de espera.
// Usa PeerNet (P2P) y el sistema de modales. Máx. 3 jugadores (anfitrión + 2).
import QRCode from 'qrcode';
import { PeerNet, MAX_PLAYERS } from '../net/PeerNet.js';
import { EVENTS } from '../utils/constants.js';
import { SaveManager } from '../core/SaveManager.js';
import friendFaceBoy from '../assets/avatar-ia-1.webp';
import friendFaceGirl from '../assets/avatar-ia-2.webp';
// QrScanner (con jsQR) se carga bajo demanda al escanear, para no engordar el bundle.

export class FriendsUI {
  constructor({ modals, bus, progression, onStart, friendNet }) {
    this.modals = modals;
    this.bus = bus;
    this.progression = progression;
    this.onStart = onStart; // (roster, {isHost}) => void  (fase de sincronización)
    this.friendNet = friendNet; // capa de amigos (invitaciones directas)
    this.net = null;
    this._offs = [];
  }

  _name() {
    return (this.progression?.profile?.name || 'Jugador').slice(0, 16);
  }

  /** Jugar online exige internet. Si no hay conexión, avisa con un modal y devuelve false. */
  _requireOnline() {
    if (navigator.onLine) return true;
    this.bus.emit(EVENTS.PLAY_SFX, { name: 'invalid' });
    const modal = this.modals.open({
      title: 'SIN CONEXIÓN',
      className: 'mode-modal modes-modal',
      dismissible: true,
      bodyHTML: `<div class="offline-note">
        <div class="offline-ic" aria-hidden="true">
          <svg viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="48" cy="48" r="44" class="oi-ring"/>
            <g class="oi-wifi">
              <path d="M18 46 Q48 12 78 46" class="oi-arc"/>
              <path d="M28 56 Q48 32 68 56" class="oi-arc"/>
              <path d="M37 66 Q48 52 59 66" class="oi-arc"/>
              <circle cx="48" cy="73" r="4.5" class="oi-dot"/>
            </g>
            <line x1="26" y1="26" x2="70" y2="70" class="oi-slash-bg"/>
            <line x1="26" y1="26" x2="70" y2="70" class="oi-slash"/>
          </svg>
        </div>
        <p class="modal-text">Necesitas <b>conexión a internet</b> para jugar online con un amigo.<br>Conéctate e inténtalo de nuevo.</p>
      </div>`,
    });
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => this.modals.close());
    modal.appendChild(closeBtn);
    return false;
  }

  /** Avatar para la red: solo emoji (las imágenes no se comparten por P2P). */
  _avatar() {
    const av = this.progression?.profile?.avatar;
    return av && av !== 'default' && av !== 'custom' ? av : '🙂';
  }

  /** Punto de entrada: elegir crear o unirse. */
  open(prefillCode = null) {
    if (prefillCode) return this._join(prefillCode);
    const nFriends = SaveManager.loadFriends().length;
    const friendsIc = `<span class="fo-faces"><img src="${friendFaceBoy}" alt=""><img src="${friendFaceGirl}" alt=""></span>`;
    const opts = [
      { act: 'friends', icHtml: friendsIc, color: '#7ed957', title: `Amigos${nFriends ? ` <span class="num-font">(${nFriends})</span>` : ''}`, desc: 'Invita directamente a un amigo guardado.' },
      { act: 'create', ic: '🎲', color: '#f2c14e', title: 'Crear sala', desc: 'Genera un código y un QR para invitar.' },
      { act: 'join', ic: '🔗', color: '#4bb6e8', title: 'Unirse', desc: 'Introduce el código de un amigo.' },
    ];
    const body = `
      <p class="sub-hint">Partida 1 contra 1 con un amigo (conexión directa P2P).</p>
      <div class="diff-grid friends-grid">${opts.map((o, i) => `
        <button class="diff-card friend-opt" data-act="${o.act}" style="--mc:${o.color};--d:${i * 0.05}s">
          ${o.icHtml || `<span class="fo-ic">${o.ic}</span>`}
          <span class="dc-label">${o.title}</span>
          <span class="dc-desc">${o.desc}</span>
        </button>`).join('')}</div>`;
    const modal = this.modals.open({
      title: 'JUGAR CON AMIGOS',
      className: 'mode-modal modes-modal friends-grid-modal',
      dismissible: true,
      bodyHTML: body,
    });
    // Cerrar con círculo + X arriba a la derecha (como en el menú principal).
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => { this._teardown(); this.modals.close(); });
    modal.appendChild(closeBtn);
    modal.querySelector('[data-act="friends"]').addEventListener('click', () => { this.modals.close(); this.openMyFriends(); });
    modal.querySelector('[data-act="create"]').addEventListener('click', () => this._create());
    modal.querySelector('[data-act="join"]').addEventListener('click', () => this._join());
  }

  // --------------------------------------------------------------------------
  // Lista de amigos + invitaciones directas
  // --------------------------------------------------------------------------
  _myCode() { return this.progression?.profile?.friendCode || '—'; }

  /** Enlace para que otro te añada como amigo. */
  _friendLink() {
    const base = `${location.origin}${location.pathname}`;
    const p = this.progression.profile;
    const q = new URLSearchParams({ friend: this._myCode(), name: p.name || 'Jugador', avatar: this._avatar() });
    return `${base}?${q.toString()}`;
  }

  openMyFriends() {
    const modal = this.modals.open({
      title: 'AMIGOS',
      className: 'mode-modal modes-modal friends-modal',
      dismissible: true,
      bodyHTML: '<div id="friends-body"></div>',
    });
    // "Atrás" como botón circular con X (vuelve a la elección).
    const backBtn = document.createElement('button');
    backBtn.className = 'modal-close';
    backBtn.setAttribute('aria-label', 'Volver');
    backBtn.innerHTML = '✕';
    backBtn.addEventListener('click', () => { this.modals.close(); this.open(); });
    modal.appendChild(backBtn);
    this._friendsModal = modal;
    this._friendsTab = 'list'; // abre en "Tus amigos"
    this._renderFriends();
  }

  _renderFriends() {
    const el = this._friendsModal?.querySelector('#friends-body');
    if (!el) return;
    const friends = SaveManager.loadFriends();
    const link = this._friendLink();
    const tab = this._friendsTab || 'list';

    // Cada pestaña a PANTALLA COMPLETA (así los nombres de la lista no se cortan).
    const qrPane = `
      <div class="fr-pane fr-pane-qr">
        <div class="qr-info">
          <span class="mc-label">Tu código de amigo</span>
          <div class="mc-code-row">
            <span class="mc-code">${this._myCode()}</span>
            <button class="mc-copy" data-copy="${link}" title="Copiar enlace" aria-label="Copiar enlace">📋</button>
          </div>
          <p class="sub-hint">Comparte tu código o el QR para que te añadan como amigo.</p>
        </div>
        <div class="mc-qr qr-big"><img id="my-code-qr" alt="QR de tu código" /></div>
      </div>`;

    const addPane = `
      <div class="fr-pane fr-pane-add">
        <p class="sub-hint">Escribe el código de un amigo o escanea su QR con la cámara.</p>
        <div class="add-friend">
          <input id="add-code" class="code-input add-code-input" maxlength="8" placeholder="CÓDIGO DE AMIGO" />
          <button class="btn btn-sm" id="scan-qr-btn" title="Escanear QR con la cámara">📷</button>
          <button class="btn btn-sm btn-primary" id="add-friend-btn">Añadir</button>
        </div>
      </div>`;

    const listPane = `
      <div class="fr-pane fr-pane-list">
        <div class="friends-list">
          ${friends.length ? friends.map((f) => `
            <div class="friend-row" data-code="${f.code}">
              <span class="fr-dot checking" data-dot="${f.code}" title="Comprobando…"></span>
              <span class="fr-ava">${f.avatar || '🙂'}</span>
              <span class="fr-name">${f.name || 'Amigo'}</span>
              <button class="btn btn-sm btn-primary fr-invite" data-invite="${f.code}">▶ Invitar</button>
              <button class="fr-del" data-del="${f.code}" title="Quitar">✕</button>
            </div>`).join('') : '<p class="hint fr-empty">Aún no tienes amigos guardados.<br>Añádelos desde la pestaña "Añadir".</p>'}
        </div>
      </div>`;

    const panes = { qr: qrPane, add: addPane, list: listPane };
    el.innerHTML = `
      <div class="fr-tabs">
        <button class="fr-tab ${tab === 'qr' ? 'on' : ''}" data-tab="qr"><span class="ft-ic">📇</span> Tu QR</button>
        <button class="fr-tab ${tab === 'add' ? 'on' : ''}" data-tab="add"><span class="ft-ic">➕</span> Añadir</button>
        <button class="fr-tab ${tab === 'list' ? 'on' : ''}" data-tab="list"><span class="ft-ic">👥</span> Tus amigos${friends.length ? ` <span class="num-font">(${friends.length})</span>` : ''}</button>
      </div>
      <div class="fr-tab-body">${panes[tab]}</div>`;

    // Cambio de pestaña.
    el.querySelectorAll('[data-tab]').forEach((b) => b.addEventListener('click', () => {
      this._friendsTab = b.getAttribute('data-tab');
      this._renderFriends();
    }));

    if (tab === 'qr') {
      // width alta (512) para que la imagen pueda escalar y LLENAR su caja cuadrada sin pixelarse.
      QRCode.toDataURL(link, { margin: 1, width: 512, color: { dark: '#241a12', light: '#f4e9d0' } })
        .then((url) => { const img = el.querySelector('#my-code-qr'); if (img) img.src = url; }).catch(() => {});
      el.querySelector('[data-copy]')?.addEventListener('click', (e) => {
        navigator.clipboard?.writeText(e.currentTarget.getAttribute('data-copy'))
          .then(() => this.modals.toast('Enlace copiado', { icon: '📋' })).catch(() => {});
      });
    } else if (tab === 'add') {
      const addInput = el.querySelector('#add-code');
      addInput.addEventListener('input', () => { addInput.value = addInput.value.toUpperCase().replace(/[^A-Z0-9]/g, ''); });
      el.querySelector('#add-friend-btn').addEventListener('click', () => this._addFriend(addInput.value));
      el.querySelector('#scan-qr-btn').addEventListener('click', () => this._scanQr());
    } else {
      el.querySelectorAll('[data-invite]').forEach((b) => b.addEventListener('click', () => this._inviteFriend(b.getAttribute('data-invite'))));
      el.querySelectorAll('[data-del]').forEach((b) => b.addEventListener('click', () => this._removeFriend(b.getAttribute('data-del'))));
      // Presencia: ping a cada amigo y actualiza su punto (verde = conectado).
      this._pingToken = (this._pingToken || 0) + 1;
      const token = this._pingToken;
      if (this.friendNet) {
        for (const f of friends) {
          this.friendNet.ping(f.code).then((online) => {
            if (token !== this._pingToken) return;
            const dot = el.querySelector(`[data-dot="${f.code}"]`);
            if (!dot) return;
            dot.classList.remove('checking');
            dot.classList.toggle('online', online);
            dot.classList.toggle('offline', !online);
            dot.title = online ? 'Conectado' : 'Desconectado';
          }).catch(() => {});
        }
      }
    }
  }

  addFriendData({ code, name, avatar }) {
    if (!code || code === this._myCode()) return false;
    const friends = SaveManager.loadFriends();
    const i = friends.findIndex((f) => f.code === code);
    const entry = { code, name: (name || 'Amigo').slice(0, 16), avatar: avatar || '🙂' };
    if (i >= 0) friends[i] = entry; else friends.push(entry);
    SaveManager.saveFriends(friends);
    this.bus.emit(EVENTS.FRIENDS_CHANGED, {});
    return true;
  }

  /** Abre la cámara para escanear el QR de un amigo (o de una sala). */
  _scanQr() {
    import('./QrScanner.js').then(({ QrScanner }) => {
      if (!this.qrScanner) this.qrScanner = new QrScanner(this.modals.root);
      this.qrScanner.open((text) => this._handleScanned(text));
    }).catch(() => this.modals.toast('No se pudo abrir el escáner.', { icon: '⚠️' }));
  }

  _handleScanned(text) {
    let params = null;
    try { params = new URL(text).searchParams; } catch { /* no es una URL */ }
    const friend = params?.get('friend');
    const join = params?.get('join');
    if (friend) {
      this.addFriendData({ code: friend.toUpperCase(), name: params.get('name'), avatar: params.get('avatar') });
      this.modals.toast(`${params.get('name') || 'Amigo'} añadido`, { icon: '✅' });
      this._renderFriends();
      return;
    }
    if (join) { this.modals.close(); this.open(join.toUpperCase()); return; }
    // Código pelado.
    const code = String(text).toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (code.length >= 6 && code !== this._myCode()) {
      this.addFriendData({ code, name: 'Amigo', avatar: '🙂' });
      this.modals.toast('Amigo añadido', { icon: '✅' });
      this._renderFriends();
    } else {
      this.modals.toast('QR no reconocido', { icon: '⚠️' });
    }
  }

  _addFriend(code) {
    code = (code || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (code.length < 6) { this.modals.toast('Código no válido', { icon: '⚠️' }); return; }
    if (code === this._myCode()) { this.modals.toast('Ese es tu propio código', { icon: '🙂' }); return; }
    this.addFriendData({ code, name: 'Amigo', avatar: '🙂' });
    this.modals.toast('Amigo añadido', { icon: '✅' });
    this._friendsTab = 'list'; // tras añadir, muestra la lista
    this._renderFriends();
  }

  _removeFriend(code) {
    SaveManager.saveFriends(SaveManager.loadFriends().filter((f) => f.code !== code));
    this.bus.emit(EVENTS.FRIENDS_CHANGED, {});
    this._renderFriends();
  }

  /** Crea una sala e invita directamente al amigo (si está conectado). */
  async _inviteFriend(friendCode) {
    if (!this._requireOnline()) return;
    this.modals.close();
    this._openConnecting('Creando sala e invitando…');
    this.net = new PeerNet(this.bus);
    try {
      await this.net.createRoom(this._name(), { avatar: this._avatar() });
    } catch {
      this.modals.close();
      this._openError('No se pudo crear la sala.');
      return;
    }
    // Envía la invitación por la red de amigos.
    let delivered = false;
    if (this.friendNet) {
      delivered = await this.friendNet.invite(friendCode, this.net.code);
    }
    this.modals.close();
    this._openLobby();
    if (delivered) this.modals.toast('Invitación enviada ✅', { icon: '📨' });
    else this.modals.toast('Tu amigo no está conectado. Comparte el enlace o el código.', { icon: 'ℹ️', duration: 3200 });
  }

  // --------------------------------------------------------------------------
  async _create() {
    if (!this._requireOnline()) return;
    this.net = new PeerNet(this.bus);
    this.modals.close();
    const modal = this._openConnecting('Creando sala…');
    try {
      await this.net.createRoom(this._name(), { avatar: this._avatar() });
      this.modals.close();
      this._openLobby();
    } catch (e) {
      this.modals.close();
      this._openError('No se pudo crear la sala. Revisa tu conexión e inténtalo de nuevo.');
    }
  }

  _join(prefill = null) {
    // Formulario de código (o auto-unión si viene por enlace/QR).
    const doJoin = async (code) => {
      if (!this._requireOnline()) return;
      this.net = new PeerNet(this.bus);
      this.modals.close();
      this._openConnecting('Conectando con la sala…');
      try {
        await this.net.joinRoom(code, this._name(), this._avatar());
        this.modals.close();
        this._openLobby();
      } catch (e) {
        this.modals.close();
        const msg = e?.message === 'no-room'
          ? 'No existe ninguna sala con ese código.'
          : 'No se pudo conectar. Comprueba el código y tu conexión.';
        this._openError(msg, () => this._join());
      }
    };
    if (prefill) return doJoin(String(prefill).toUpperCase());

    this.modals.close(); // cierra el modal de elección antes de mostrar el formulario
    const body = `
      <p class="sub-hint">Escribe el código que te ha pasado tu amigo.</p>
      <input id="room-code" class="code-input" maxlength="5" autocomplete="off"
        placeholder="ABC12" aria-label="Código de sala" />`;
    const modal = this.modals.open({
      title: 'UNIRSE A UNA SALA',
      className: 'mode-modal modes-modal',
      dismissible: true,
      bodyHTML: body,
    });
    // Atrás: círculo con X arriba a la derecha (vuelve a la elección).
    const backBtn = document.createElement('button');
    backBtn.className = 'modal-close';
    backBtn.setAttribute('aria-label', 'Volver');
    backBtn.innerHTML = '✕';
    backBtn.addEventListener('click', () => { this.modals.close(); this.open(); });
    modal.appendChild(backBtn);
    // Entrar: círculo con ▶, abajo a la derecha, con rebote (como en Crear sala).
    const enterBtn = document.createElement('button');
    enterBtn.className = 'lobby-start';
    enterBtn.setAttribute('aria-label', 'Entrar');
    enterBtn.innerHTML = '▶';
    enterBtn.disabled = true; // No se activa hasta que haya un código válido.
    enterBtn.addEventListener('click', () => {
      const v = modal.querySelector('#room-code').value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (v.length < 4) return;
      doJoin(v);
    });
    modal.appendChild(enterBtn);
    const input = modal.querySelector('#room-code');
    input.addEventListener('input', () => {
      input.value = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      enterBtn.disabled = input.value.length < 4; // Play activo solo con código.
    });
    input.focus();
  }

  // --------------------------------------------------------------------------
  _openLobby() {
    this._teardownListeners();
    const isHost = this.net.isHost;
    const modal = this.modals.open({
      title: 'SALA DE ESPERA',
      className: 'mode-modal modes-modal lobby-modal',
      dismissible: false,
      bodyHTML: `<div id="lobby-body"></div>`,
    });
    // Cerrar/Salir como botón circular con X arriba a la derecha.
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.setAttribute('aria-label', 'Volver');
    closeBtn.innerHTML = '✕';
    // Volver a "JUGAR CON AMIGOS" (abandona la sala), no al menú principal.
    closeBtn.addEventListener('click', () => { this._teardown(); this.modals.close(); this.open(); });
    modal.appendChild(closeBtn);
    // Botón EMPEZAR (solo anfitrión): círculo con ▶, más grande, con rebote, abajo-derecha.
    if (isHost) {
      const startBtn = document.createElement('button');
      startBtn.className = 'lobby-start act-start';
      startBtn.setAttribute('aria-label', 'Empezar');
      startBtn.innerHTML = '▶';
      startBtn.addEventListener('click', () => this._start());
      modal.appendChild(startBtn);
    }
    this.lobbyModal = modal;
    this._renderLobby();

    this._offs.push(this.bus.on(EVENTS.NET_ROSTER, () => this._renderLobby()));
    this._offs.push(this.bus.on(EVENTS.NET_STARTED, (d) => this._onStarted(d)));
    // El invitado sale de la sala (cierre de conexión): avisa al anfitrión.
    this._offs.push(this.bus.on(EVENTS.NET_PEER_LEFT, () => { this._teardown(); this._openError('Tu rival ha salido de la sala.'); }));
    this._offs.push(this.bus.on(EVENTS.NET_CLOSED, (d) => {
      let msg = 'La sala se ha cerrado.';
      if (d?.reason === 'full') msg = 'La sala está llena.';
      else if (d?.reason === 'version') msg = 'Los dos dispositivos tienen versiones distintas del juego. Actualizad ambos: cerrad la app y volved a abrirla (o borrad los datos del sitio en el navegador) para cargar la última versión.';
      else if (d?.reason === 'opponent-left' || d?.reason === 'host-left') msg = `${d?.name || 'Tu rival'} ha salido de la sala.`;
      this._teardown(); this._openError(msg);
    }));
    // El amigo invitado RECHAZA la invitación: avisa al anfitrión y vuelve a "JUGAR CON AMIGOS".
    this._offs.push(this.bus.on(EVENTS.FRIEND_REJECT, (d) => {
      const who = d?.from?.name || 'Tu amigo';
      this._teardown();
      this.modals.closeAll();           // cierra la sala de espera
      this.open();                      // vuelve a JUGAR CON AMIGOS
      this.modals.toast(`${who} ha rechazado la invitación.`, { icon: '🚫', duration: 3600 });
    }));
  }

  _renderLobby() {
    const el = this.lobbyModal?.querySelector('#lobby-body');
    if (!el) return;
    const isHost = this.net.isHost;
    const roster = this.net.roster;
    const link = this.net.shareLink();

    const playersCol = `
      <div class="lobby-players">
        <div class="side-title"><span class="dot"></span> Jugadores <span class="side-count">${roster.length}/${MAX_PLAYERS}</span></div>
        <div class="lobby-list">
          ${roster.map((p) => `
            <div class="lobby-player">
              <span class="lp-avatar">${p.avatar || (p.host ? '👑' : '🙂')}</span>
              <span class="lp-name">${p.name}${p.id === this.net.selfId ? ' (tú)' : ''}</span>
              ${p.host ? '<span class="lp-tag">Anfitrión</span>' : ''}
            </div>`).join('')}
          ${Array.from({ length: MAX_PLAYERS - roster.length }).map(() => `
            <div class="lobby-player empty"><span class="lp-avatar">➕</span><span class="lp-name">Esperando…</span></div>`).join('')}
        </div>
      </div>`;

    el.innerHTML = isHost ? `
      <div class="fr-cols">
        <div class="fr-left">
          <div class="lobby-share">
            <span class="lobby-code-label">Código de sala</span>
            <span class="lobby-code">${this.net.code}</span>
            <div class="lobby-qr"><img id="lobby-qr-img" alt="QR para unirse" /></div>
            <button class="btn btn-sm lobby-copy" data-copy="${link}">📋 Copiar enlace</button>
            <p class="sub-hint">Comparte el código o el QR para que se unan.</p>
          </div>
        </div>
        <div class="fr-right">${playersCol}</div>
      </div>` : `
      <p class="sub-hint">Conectado a la sala <b>${this.net.code}</b>. Esperando al anfitrión…</p>
      ${playersCol}`;

    if (isHost) {
      const img = el.querySelector('#lobby-qr-img');
      // width alta (512) para que la imagen pueda escalar y LLENAR su caja cuadrada sin pixelarse.
      QRCode.toDataURL(link, { margin: 1, width: 512, color: { dark: '#241a12', light: '#f4e9d0' } })
        .then((url) => { if (img) img.src = url; }).catch(() => {});
      el.querySelector('.lobby-copy')?.addEventListener('click', (e) => {
        const l = e.currentTarget.getAttribute('data-copy');
        navigator.clipboard?.writeText(l).then(() => this.modals.toast('Enlace copiado', { icon: '📋' })).catch(() => {});
      });
      // Habilita "Empezar" solo con 2+ jugadores.
      const startBtn = this.lobbyModal.querySelector('.act-start');
      if (startBtn) startBtn.disabled = roster.length < 2;
    }
  }

  _start() {
    if (!this.net?.isHost) return true;
    if (this.net.roster.length < 2) return true;
    this.net.startGame({ ts: Date.now() });
    return true; // el flujo de inicio lo maneja _onStarted
  }

  _onStarted(d) {
    // La sincronización de la partida en sí llega en la siguiente fase.
    this._teardownListeners();
    this.modals.close();
    if (this.onStart) this.onStart(this.net, { isHost: !!d?.host });
  }

  // --------------------------------------------------------------------------
  _openConnecting(text) {
    return this.modals.open({
      title: text,
      dismissible: false,
      bodyHTML: `<div class="connecting"><div class="spinner"></div><p class="sub-hint">${text}</p></div>`,
    });
  }

  _openError(msg, retry = null) {
    this.modals.open({
      title: '⚠️ Vaya…',
      dismissible: true,
      bodyHTML: `<p class="modal-text">${msg}</p>`,
      buttons: [
        { label: 'Cerrar', className: 'btn-ghost' },
        ...(retry ? [{ label: 'Reintentar', className: 'btn-primary', onClick: () => { retry(); return true; } }] : []),
      ],
    });
  }

  _teardownListeners() {
    for (const off of this._offs) off?.();
    this._offs = [];
  }

  _teardown() {
    this._teardownListeners();
    // Avisa al otro jugador de que sales de la sala antes de cerrar.
    try { this.net?.announceLeave(); } catch { /* noop */ }
    this.net?.leave();
    this.net = null;
  }
}
